import React, { useRef } from 'react'
import { WealthboxWorkflow, WealthboxWorkflowTemplate } from '../../../../../../../models/Integration'
import { User } from '../../../../../../../models/User'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useCreateIntegrationEntityMutation } from '../../../../../../../store/integrations/integrations'
import { useOutsideClick } from '../../../../../../../common/useOutsideClick'
import { useOverlayContainer } from '../../../../../../../common/useOverlayContainer'
import { addToast } from '../../../../../../../store/toast/toastSlice'
import { Form, Overlay } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { FormFeedback } from '../../../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../../../components/Button/Button'

interface StartWorkflowOverlayProps {
  targetRef: React.RefObject<HTMLElement | null>
  show: boolean
  onHide: () => void
  onWorkflowStarted?: (workflow: WealthboxWorkflow) => void
  workflowTemplate: WealthboxWorkflowTemplate
  contact: User
  serviceId: string
  suggestedLabel?: string
}

interface StartWorkflowFormValues {
  label: string
  startsAt: Date
  linkedTo: { id?: string; type: string }
  workflowMilestones: { id: string; milestoneDate: Date | null; name?: string | null }[]
}

export const StartWealthboxWorkflowOverlay: React.FC<StartWorkflowOverlayProps> = ({
  targetRef,
  show,
  onHide,
  onWorkflowStarted,
  workflowTemplate,
  contact,
  serviceId,
  suggestedLabel,
}: StartWorkflowOverlayProps) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const integration = contact.integrations!.find((i) => i.serviceId === serviceId)
  const defaultValues = {
    label: suggestedLabel ?? '',
    linkedTo: { id: integration?.externalId, type: 'Contact' },
    startsAt: new Date(),
    workflowMilestones:
      workflowTemplate.workflowMilestones.map((milestone) => {
        return { id: milestone.id, name: milestone.name, milestoneDate: null }
      }) ?? [],
  }

  const { control, handleSubmit, reset } = useForm<StartWorkflowFormValues>({ defaultValues })
  const { fields: milestones } = useFieldArray({
    control,
    name: 'workflowMilestones',
    rules: {
      validate: (milestones) => {
        if (milestones.every((milestone) => !!milestone.milestoneDate)) {
          return
        }
        return 'Milestone dates must be selected'
      },
    },
  })

  const overlayRef = useRef(null)
  const overlayContainer = useOverlayContainer(targetRef)

  const [triggerCreateIntegrationEntity, { isLoading: isCreatingWorkflow }] = useCreateIntegrationEntityMutation()

  useOutsideClick(overlayRef, () => {
    onHide()
  })

  const _onStartWorkflow = async (formValues: StartWorkflowFormValues) => {
    if (!formValues.linkedTo.id) {
      return
    }
    try {
      const { data: workflow } = await triggerCreateIntegrationEntity({
        accountId,
        connector: serviceId,
        entityType: 'workflow',
        relatedTo: {
          id: formValues.linkedTo.id,
        },
        payload: {
          workflow_template: workflowTemplate.id,
          linked_to: formValues.linkedTo,
          label: formValues.label,
          starts_at: formValues.startsAt.toISOString(),
          workflow_milestones: formValues.workflowMilestones.map((milestone) => {
            return { id: milestone.id, milestone_date: milestone.milestoneDate }
          }),
        },
      }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Success', messages: ['The workflow has started successfully.'] }))
      reset()
      onWorkflowStarted?.({
        id: workflow.id,
        activeStep: workflow.activeStep,
        name: workflow.name,
        description: workflow.description,
        label: workflow.label,
      } as WealthboxWorkflow)
    } catch {
      // Failed API calls are tracked centrally; nothing else to report here
    }
  }

  const onStartWorkflow = tracked(
    _onStartWorkflow,
    {
      eventName: TrackEvent.START_NEW_WORKFLOW_IN_CRM,
    },
    {
      eventDetailsExtractor: (formValues: StartWorkflowFormValues) => {
        return {
          connector: serviceId,
          contactId: contact.id,
          label: formValues.label,
          workflowMilestones: JSON.stringify(formValues.workflowMilestones),
        }
      },
    },
  )

  return (
    <Overlay ref={overlayRef} target={targetRef} container={overlayContainer} show={show} placement='left'>
      {({ ref, style }) => (
        <div ref={ref} style={style} className='start-workflow-overlay d-flex flex-column gap-3 border-default zocks-border-0.25 p-4'>
          <h4>Start {workflowTemplate.name}</h4>
          <Controller
            name='label'
            control={control}
            render={({ field }) => (
              <div>
                <Form.Label>Label</Form.Label>
                <Form.Control id={field.name} placeholder='Label…' {...field} value={field.value} onChange={field.onChange} />
              </div>
            )}
          />
          <Controller
            name='startsAt'
            control={control}
            render={({ field }) => (
              <div className='d-flex flex-column'>
                <Form.Label>Starts at</Form.Label>
                <DatePicker
                  className='form-control'
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  todayButton='Today'
                  showTimeSelect
                  dateFormat='MM/dd/yyyy h:mm aa'
                  placeholderText='Starts at…'
                />
              </div>
            )}
          />
          {milestones.map((milestone, rowIndex) => {
            return (
              <Controller
                key={milestone.id}
                name={`workflowMilestones.${rowIndex}.milestoneDate`}
                control={control}
                rules={{ required: 'Milestone date is required' }}
                render={({ field, fieldState }) => (
                  <div className='d-flex flex-column'>
                    <Form.Label>{milestone.name}</Form.Label>
                    <DatePicker
                      className='form-control'
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      todayButton='Today'
                      showTimeSelect
                      dateFormat='MM/dd/yyyy h:mm aa'
                      placeholderText='Milestone date…'
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </div>
                )}
              />
            )
          })}
          <div className='align-self-end d-flex gap-2'>
            <Button variant='secondary' className='button-small' onClick={onHide}>
              Cancel
            </Button>
            <Button variant='primary' className='button-small' onClick={handleSubmit(onStartWorkflow)} loading={isCreatingWorkflow}>
              <i className='bi bi-play' />
              Start
            </Button>
          </div>
        </div>
      )}
    </Overlay>
  )
}
